import React, { useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { Grid, Button, Stack } from '@mui/material';
import { TimeFieldWrapper } from 'components/Input/TimeFieldWrapper';
import { DatePickerWrapper } from 'components/Input/DatePickerWrapper';
import { TextFieldWrapper } from 'components/Input/TextFieldWrapper';
import { SelectWrapper } from 'components/Input/SelectWrapper';
import {
  CREATE_DEBATE_FORM_NAMES,
  CREATE_DEBATE_FORM_LABELS,
  CREATE_DEBATE_FORM_VALUES,
} from 'shared/constants/form-constants';
import { FORMS_BUTTON } from 'shared/constants/buttons-constants';
import { RESPONSE_ATTRIBUTES } from 'shared/constants/validation-constants';
import { useGetProfileQuery } from 'redux/api/user.api';
import { useCreateChallengeMutation } from 'redux/api/challenge.api';
import { useLoading } from 'shared/hooks/useLoading';
import { urls } from 'shared/constants/routing.constants';
import { TError } from 'shared/types/api.interface';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingLayer from 'components/LoadingLayer/LoadingLayer';
import { useAppSelector } from 'redux/hooks/reduxHooks';
import { selectCurrentModal } from 'redux/selectors/selectors';
import { TCreateDebateFormType, CreateDebateFormValidation } from './validationSchema';

const fieldsArr = [
  CREATE_DEBATE_FORM_NAMES.date,
  CREATE_DEBATE_FORM_NAMES.time,
  CREATE_DEBATE_FORM_NAMES.thesis,
  CREATE_DEBATE_FORM_NAMES.description,
];

export const CreateDebateForm = () => {
  const currentModal = useAppSelector(selectCurrentModal);
  const initialData = currentModal.data as { title?: string; subtitle?: string } | undefined;

  const {
    handleSubmit,
    reset,
    control,
    setError,
    setValue,
    formState: { errors },
  } = useForm<TCreateDebateFormType>({
    defaultValues: {
      // @ts-ignore
      [CREATE_DEBATE_FORM_NAMES.date]: dayjs().startOf('day'),
      // @ts-ignore
      [CREATE_DEBATE_FORM_NAMES.time]: dayjs().startOf('minute').add(5, 'minute'),
      [CREATE_DEBATE_FORM_NAMES.round]: CREATE_DEBATE_FORM_VALUES.round[2],
      [CREATE_DEBATE_FORM_NAMES.duration]: CREATE_DEBATE_FORM_VALUES.duration[1],
      [CREATE_DEBATE_FORM_NAMES.thesis]: '',
      [CREATE_DEBATE_FORM_NAMES.description]: '',
    },
    resolver: zodResolver(CreateDebateFormValidation),
  });

  useEffect(() => {
    if (initialData?.title) {
      setValue(CREATE_DEBATE_FORM_NAMES.thesis, initialData.title);
    }
    if (initialData?.subtitle) {
      setValue(CREATE_DEBATE_FORM_NAMES.description, initialData.subtitle);
    }
  }, [initialData, setValue]);

  const navigate = useNavigate();
  const location = useLocation();
  const { data: profileData = null } = useGetProfileQuery();
  const [createChallenge, { isLoading }] = useCreateChallengeMutation();
  useLoading(isLoading);

  const getChallengeStartDateTime = (date: Date, time: Date) => {
    const dateUnix = Math.floor(new Date(date).getTime() / 1000);
    const timeWithTodayDateUnix = Math.floor(new Date(time).getTime() / 1000);
    const today = new Date();
    const todayExceptHours = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      0,
      0,
      0,
    );
    const todayExceptHoursUnix = Math.floor(todayExceptHours.getTime() / 1000);

    const timeUnix = timeWithTodayDateUnix - todayExceptHoursUnix;

    return dateUnix + timeUnix;
  };

  const catchSubmitError = (errorsArr: TError[]) => {
    fieldsArr.forEach((field) => {
      setError(field, {
        type: 'custom',
        message:
          errorsArr.find((item: TError) => {
            if (
              field === CREATE_DEBATE_FORM_NAMES.date ||
              field === CREATE_DEBATE_FORM_NAMES.time
            ) {
              return item.attr === RESPONSE_ATTRIBUTES.startDatetime;
            }
            return item.attr === field;
          })?.detail || '',
      });
    });
  };

  const onSubmit: SubmitHandler<TCreateDebateFormType> = async (
    formDdata: TCreateDebateFormType,
  ) => {
    try {
      const { thesis, description, date, time, round, duration } = formDdata;

      const challengeStartDateTimeUnix = getChallengeStartDateTime(date, time);

      const newChallenge = await createChallenge({
        thesis,
        description,
        auto_start: false,
        initiator: profileData?.id,
        start_datetime: challengeStartDateTimeUnix,
        debate_settings: {
          rounds_per_participant: round,
          round_duration: duration * 60,
        },
      }).unwrap();
      if (!location.pathname.startsWith(urls.myPages.video)) {
        navigate(`${urls.commonPages.challengeCard}/${newChallenge.id}`);
      }
      reset();
    } catch (err) {
      catchSubmitError(err?.data?.errors);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column' }}>
      <Stack
        sx={{
          alignItems: 'stretch',
          rowGap: { lg: '37px', md: '40px', sm: '40px', xs: '40px' },
        }}
      >
        <Grid
          container
          spacing="20px"
          sx={{
            rowGap: { md: '17px', sm: '20px', xs: '20px' },
          }}
        >
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <DatePickerWrapper
              name={CREATE_DEBATE_FORM_NAMES.date}
              control={control}
              label={CREATE_DEBATE_FORM_LABELS.date}
              errorMessage={errors.date ? (errors.date?.message as string) : ''}
            />
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <TimeFieldWrapper
              name={CREATE_DEBATE_FORM_NAMES.time}
              control={control}
              label={CREATE_DEBATE_FORM_LABELS.time}
              errorMessage={errors.time ? (errors.time?.message as string) : ''}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectWrapper
              name={CREATE_DEBATE_FORM_NAMES.round}
              control={control}
              label={CREATE_DEBATE_FORM_LABELS.round}
              values={CREATE_DEBATE_FORM_VALUES.round}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectWrapper
              name={CREATE_DEBATE_FORM_NAMES.duration}
              control={control}
              label={CREATE_DEBATE_FORM_LABELS.duration}
              values={CREATE_DEBATE_FORM_VALUES.duration}
              valueText="min"
            />
          </Grid>
        </Grid>
        <TextFieldWrapper
          name={CREATE_DEBATE_FORM_NAMES.thesis}
          control={control}
          label={CREATE_DEBATE_FORM_LABELS.thesis}
          errorMessage={errors.thesis ? (errors.thesis?.message as string) : ''}
        />
        <TextFieldWrapper
          name={CREATE_DEBATE_FORM_NAMES.description}
          control={control}
          label={CREATE_DEBATE_FORM_LABELS.description}
          errorMessage={errors.description ? (errors.description?.message as string) : ''}
          multiline
          rows={6}
        />
      </Stack>
      <Button
        variant="fixedSize"
        type="submit"
        sx={{ mt: { lg: '56px', md: '24px', sm: '24px', xs: '24px' }, alignSelf: 'center' }}
      >
        {FORMS_BUTTON.createDebateSubmitButton}
      </Button>
      <LoadingLayer />
    </form>
  );
};
